import styles from './styles.module.scss';

import { EditorState } from 'draft-js';
import { isEmpty, isEqual } from 'lodash';
import { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AlertDialog, AlertDialogType } from '../../../../../../components/AlertDialog';
import { Board } from '../../../../../../components/Board';
import { NavButton } from '../../../../../../components/NavButton';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { TextArea } from '../../../../../../components/TextArea';
import { TextEditor } from '../../../../../../components/TextEditor';
import {
    convertDraftContentToHTML,
    convertHtmlToDraftContent
} from '../../../../../../components/TextEditor/utils';
import { verifyRoutePermissions } from '../../../../../../globalFunctions';
import { useLogicManagerModule } from '../../../../../../hooks/useLogicManagerModule';
import { LogicManagerModule } from '../../../../../../modules/config/LogicManagerModulesList';
import { getModuleByProp } from '../../../../../../modules/config/ModulesList';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import { Exercise, User, LoadData } from '../../../../../../types';
import { saveModuleChanges, saveExerciseChanges } from './requestHandlers';

type ModuleOriginalData = {
    modal: string;
    exercise: Exercise;
};

export const LogicManagerModuleDetailRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const navigate = useNavigate();

    // Modal
    const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());

    // Loading exercise dialogs
    const [modulesStatus, setModulesStatus] = useState([]);
    const { moduleId } = useParams();
    
    const logicManagerModule: LogicManagerModule = getModuleByProp(parseInt(moduleId||"1") ,modulesStatus);
    const logicManagerModuleData: LoadData = useLogicManagerModule(logicManagerModule.slug);
    const [selectedExercise, setSelectedExercise] = useState<Exercise>({} as Exercise);

    // Handle changes
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [originalData, setOriginalData] = useState<ModuleOriginalData>({
        modal: '',
        exercise: {} as Exercise
    });

    const goBackwards = () => {
        if (logicManagerModuleData.isLoading) {
            return;
        } else if (convertDraftContentToHTML(editorState) !== originalData.modal) {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: resetChanges,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: onSave,
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        } else if (
            !isEmpty(originalData.exercise) &&
            !isEqual(selectedExercise, originalData.exercise)
        ) {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: resetChanges,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: onSave,
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        } else {
            navigate(`/manager/edit/modules`);
        }
    };

    const renderOption = (exercise: Exercise,index: number) => {
        return ( exercise &&
            <option key={exercise.id} value={exercise.sort}>
                {index} - {exercise.name}
            </option>
        );
    };

    const loadRenderOptions = () => {
        const data = logicManagerModuleData.data;
        const options = [] as JSX.Element[];
        var index = 1
        for (const sort of Object.keys(data.exercises)) {
            options.push(renderOption(data.exercises[sort] as Exercise,index));
            index++
        }

        return options;
    };

    const handleChangeExercise = (sort: number | string) => {
        if (!isEmpty(selectedExercise) && !isEqual(selectedExercise, originalData.exercise)) {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: resetChanges,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: onSave,
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        } else {
            const data = logicManagerModuleData.data;
            let exerciseMock = {} as Exercise;

            for (const exerciseSort of Object.keys(data.exercises)) {
                if (data.exercises[exerciseSort].sort == sort) {
                    exerciseMock = Object.assign({}, data.exercises[exerciseSort] as Exercise);
                    break;
                }
            }
            exerciseMock.description = exerciseMock.description? exerciseMock.description : "Resolva este exercicio de lógica de programação"
            exerciseMock.description = exerciseMock.description.replace(/<br>/gm, '\n');

            setSelectedExercise(exerciseMock);
            originalData.exercise = exerciseMock;
        }
    };

    const handleChangeDialog = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const exercise = Object.assign({}, selectedExercise);
        exercise.description = event.target.value;
        setSelectedExercise(exercise);
    };

    const handleChangeJson = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const exercise = Object.assign({}, selectedExercise);
        exercise.json = event.target.value;
        setSelectedExercise(exercise);
    };

    const prepareModule = (modal: string) => {
        const preparedModule = {
            id: logicManagerModuleData.data.id,
            modal: modal
        };

        return preparedModule;
    };

    const prepareExercise = (exercise: Exercise) => {
        const description = exercise.description? exercise.description
            .replace(/[\r\n]+$/, '')
            .replace(/(\r\n|\n|\r)/gm, '<br>')
            .trim() : '' ;

        const preparedExercise = {
            id: exercise.id,
            description: description,
            json: exercise.json
        };

        return preparedExercise;
    };

    const onSave = async () => {
        try {
            setIsLoadingContent(true);

            const modalContent = convertDraftContentToHTML(editorState);
            let hasSaved = false;

            // Saving modal
            if (modalContent !== originalData.modal) {
                const preparedModule = prepareModule(modalContent) as LogicManagerModule;
                const result = await saveModuleChanges(userAuth.token, navigate, preparedModule);

                if (
                    result &&
                    typeof result !== 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    originalData.modal = modalContent;
                    hasSaved = true;
                } else {
                    throw result as Error;
                }
            }

            // Saving exercise
            if (!isEqual(selectedExercise, originalData.exercise)) {
                const preparedExercise = prepareExercise(selectedExercise as Exercise) as Exercise;
                const result = await saveExerciseChanges(
                    userAuth.token,
                    navigate,
                    preparedExercise
                );

                if (
                    result &&
                    typeof result !== 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    const data = logicManagerModuleData.data;

                    for (const sort of Object.keys(data.exercises)) {
                        if (data.exercises[sort].id === selectedExercise.id) {
                            data.exercises[sort].description = selectedExercise.description;
                            data.exercises[sort].json = selectedExercise.json;
                            break;
                        }
                    }

                    originalData.exercise = selectedExercise;
                    hasSaved = true;
                } else {
                    throw result as Error;
                }
            }

            if (hasSaved) {
                setMessage({
                    type: 'success',
                    content: 'Alterações salvas com sucesso'
                });
                setOpenMessage(true);
            } else {
                setMessage({
                    type: 'info',
                    content: 'Sem alterações para salvar'
                });
                setOpenMessage(true);
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: error.message
            });
            setOpenMessage(true);
        } finally {
            setIsLoadingContent(false);
        }
    };

    const resetChanges = () => {
        setEditorState(convertHtmlToDraftContent(originalData.modal));
        setSelectedExercise(originalData.exercise);
    };

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.admin'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    useEffect(() => {
        if (!logicManagerModuleData.isLoading) {
            const data = logicManagerModuleData.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                // Set modal data
                setEditorState(convertHtmlToDraftContent(data.modal));
                originalData.modal = data.modal;

                // Set exercise data
                const exercisesKeys = Object.keys(data.exercises);
                handleChangeExercise(exercisesKeys[0]);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [logicManagerModuleData.isLoading]);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && !isEmpty(selectedExercise) && (
                <>
                    <ProfileBox user={userAuth} />
                    <Board wrapperClass={styles.board}>
                        <div className={styles.boardWrapper}>
                            <h1 className={styles.moduleName}>{logicManagerModule.name}</h1>

                            <div className={styles.fieldWrapper}>
                                <label className={styles.centralize}>Modal</label>
                                <TextEditor
                                    editorState={editorState}
                                    setEditorState={setEditorState}
                                />
                            </div>

                            <div className={styles.fieldWrapper}>
                                <label htmlFor='exercise'>Número do exercício</label>

                                <select
                                    name='exercise'
                                    id='exercise'
                                    value={selectedExercise.sort}
                                    onChange={event => handleChangeExercise(event.target.value)}
                                >
                                    {loadRenderOptions()}
                                </select>
                            </div>

                            <div className={styles.fieldWrapper}>
                                <label htmlFor='description'>Descrição do exercício</label>

                                <TextArea
                                    id='description'
                                    value={selectedExercise.description}
                                    onChange={handleChangeDialog}
                                    rows={4}
                                />
                            </div>

                            <div className={styles.fieldWrapper}>
                                <label htmlFor='json'>Código da Programação [JSON] </label>

                                <TextArea
                                    id='json'
                                    value={selectedExercise.json || ""}
                                    onChange={handleChangeJson}
                                    rows={20}
                                />
                            </div>

                            <div className={styles.buttonWrapper}>
                                <button type='button' onClick={onSave}>
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </Board>
                </>
            )}
            {alertDialogProps && (
                <AlertDialog
                    setOpen={setOpenAlert}
                    open={openAlert}
                    title={alertDialogProps.title}
                    message={alertDialogProps.message}
                    options={alertDialogProps.options}
                />
            )}
        </div>
    );
};
